import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { fmtEUR, fmtDate } from "@/lib/format";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { Search, ShoppingCart, ChevronRight, Plus } from "lucide-react";
import { useSortable } from "@/lib/sortable";
import { usePersistedState } from "@/lib/persist";
import { slaOverzichtNavOp } from "@/lib/overzichtNav";
import { InkoopStatusBadge } from "@/lib/inkoopStatus";
import { toast } from "sonner";
import { LaadBalk, TabelLaadRij } from "@/components/LaadIndicator";

function NieuweInkoopOrderDialog() {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [leveranciers, setLeveranciers] = useState([]);
  const [levId, setLevId] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open) { api.get("/leveranciers").then(r => setLeveranciers(r.data || [])); setLevId(""); }
  }, [open]);

  const aanmaken = async () => {
    if (!levId) { toast.error("Kies een leverancier"); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/inkooporders", { leverancier_id: levId, regels: [] });
      toast.success(`Inkooporder ${data.nummer} aangemaakt`);
      navigate(`/inkooporders/${data.id}`);
    } catch { toast.error("Aanmaken mislukt"); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button data-testid="nieuwe-inkooporder-btn"><Plus className="h-4 w-4 mr-1" /> Nieuwe inkooporder</Button>
      </DialogTrigger>
      <DialogContent data-testid="nieuwe-inkooporder-dialog">
        <DialogHeader><DialogTitle>Nieuwe inkooporder</DialogTitle></DialogHeader>
        <div className="space-y-2">
          <Label>Leverancier</Label>
          <Select value={levId} onValueChange={setLevId}>
            <SelectTrigger data-testid="nieuwe-inkooporder-leverancier"><SelectValue placeholder="Kies leverancier…" /></SelectTrigger>
            <SelectContent className="max-h-72">
              {leveranciers.map(l => <SelectItem key={l.id} value={l.id}>{l.naam}</SelectItem>)}
            </SelectContent>
          </Select>
          <p className="text-xs text-muted-foreground">Na aanmaken voeg je artikelen toe via 'Artikelen beheren' op de orderpagina.</p>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)}>Annuleren</Button>
          <Button onClick={aanmaken} disabled={busy || !levId} data-testid="nieuwe-inkooporder-aanmaken">{busy ? "Bezig…" : "Aanmaken"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function Inkooporders() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [q, setQ] = usePersistedState("inkooporders:q", "");
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    api.get("/inkooporders").then(r => setItems(r.data)).finally(() => setLoading(false));
  }, []);

  const filtered = items.filter(o => {
    if (!q) return true;
    const t = q.toLowerCase();
    return (o.nummer || "").toLowerCase().includes(t)
      || (o.leverancier_naam || "").toLowerCase().includes(t);
  });
  const { sortedRows, SortHeader } = useSortable(filtered, "created_at", "desc");
  const openOrder = (oid) => {
    slaOverzichtNavOp("inkooporders", sortedRows.map(o => o.id), window.location.pathname + window.location.search);
    navigate(`/inkooporders/${oid}`);
  };

  return (
    <div className="space-y-6" data-testid="inkoop-page">
      <div className="flex items-start justify-between gap-4">
        <div>
          <div className="text-sm text-muted-foreground">Inkoop</div>
          <h1 className="font-display text-4xl font-medium tracking-tight mt-1">Inkooporders</h1>
          <p className="text-sm text-muted-foreground mt-1">{filtered.length} order(s)</p>
        </div>
        <NieuweInkoopOrderDialog />
      </div>
      {loading && <LaadBalk label="Inkooporders laden" />}
      <div className="bg-card border border-border rounded-xl p-3">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input className="pl-9" placeholder="Zoek op nummer of leverancier…" value={q} onChange={(e)=>setQ(e.target.value)} data-testid="inkoop-search" />
        </div>
      </div>
      <div className="bg-card border border-border rounded-xl overflow-hidden">
        <table className="w-full">
          <thead className="bg-secondary/50 text-xs uppercase">
            <tr>
              <SortHeader k="nummer">Nummer</SortHeader>
              <SortHeader k="leverancier_naam">Leverancier</SortHeader>
              <SortHeader k="status">Status</SortHeader>
              <SortHeader k="aantal_regels" align="right">Regels</SortHeader>
              <SortHeader k="totaal" align="right" accessor={(o)=>Number(o.totaal||0)}>Totaal</SortHeader>
              <SortHeader k="created_at">Datum</SortHeader>
              <th></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {sortedRows.map(o => (
              <tr key={o.id} className="hover:bg-secondary/40 cursor-pointer" onClick={()=>openOrder(o.id)} data-testid={`inkoop-row-${o.id}`}>
                <td className="p-3 font-mono flex items-center gap-2"><ShoppingCart className="h-4 w-4 text-primary" /> {o.nummer}</td>
                <td className="p-3">{o.leverancier_naam || "—"}</td>
                <td className="p-3">
                  <div className="flex items-center gap-2">
                    <InkoopStatusBadge status={o.status} />
                    {o.dagen_te_laat > 0 && (
                      <span className="inline-flex items-center rounded-full border border-red-200 bg-red-100 px-2 py-0.5 text-xs font-medium text-red-700"
                        data-testid={`inkoop-te-laat-${o.id}`}>
                        {o.dagen_te_laat} dag{o.dagen_te_laat === 1 ? "" : "en"} te laat
                      </span>
                    )}
                  </div>
                </td>
                <td className="p-3 text-right">{o.aantal_regels}</td>
                <td className="p-3 text-right font-medium">{fmtEUR(o.totaal)}</td>
                <td className="p-3 text-muted-foreground">{fmtDate(o.created_at)}</td>
                <td className="p-3 text-right text-muted-foreground"><ChevronRight className="h-4 w-4 inline" /></td>
              </tr>
            ))}
            {sortedRows.length === 0 && <TabelLaadRij colSpan={7} loading={loading} leeg="Geen inkooporders." />}
          </tbody>
        </table>
      </div>
    </div>
  );
}
